import {Back, Elastic} from "gsap";
import {IReelConfig} from "../../../core/reel/IData/IReelData";
import {IWinRatios} from "./IWinConfig";
import {CustomEase} from "gsap/dist/CustomEase";
import {ReelSetBuilder} from "../../../core/utils/ReelSetBuilder"
import {BasicSymbolType, ISymbolDim} from "../../../core/reel/symbol/config/IReelSymbolStruct";
import {BaseServerData} from "../../server/base/IData";
import {PunkRocker} from "../../PunkRocker";

interface IGameDim {
    readonly landscape: {
        WIDHT: number;
        HEIGHT: number;
    };
    readonly portrait: {
        WIDHT: number;
        HEIGHT: number;
    };
}

class IBaseConfig {
    public static readonly BASE_DIM: IGameDim;
    public static readonly DEFAULT_SYMBOL_DIM: ISymbolDim;
    public static readonly REEL_ENGINE_CONFIG: IReelConfig;
    public static readonly WIN_RATIOS: IWinRatios;
    public static readonly NO_WIN_GAME_DATA: any;
}

export class GameConfig implements IBaseConfig {

    public static readonly GLOBAL_SCALING_SFS: number = 0.91;
    public static readonly GLOBAL_SCALING_MG_FS: number = 1;
    public static readonly FRAME_EXPAND_DUR: number = 0.334;
    public static readonly WIN_CAP: number = 30144;
    public static readonly BOTTOM_TRAIN_MULT: string[] = ["X2", "X3", "X5", "X10", "X15", "X25", "X50", "X75", "X100"];

    // public static instance:GameConfig = new GameConfig();
    public static readonly BASE_DIM: IGameDim = {
        landscape: {
            WIDHT: 1280, HEIGHT: 720
        },
        portrait: {
            WIDHT: 600, HEIGHT: 720
        }
    }

    public static readonly DEFAULT_SYMBOL_DIM: ISymbolDim = {
        WIDTH: 110,
        HEIGHT: 180,
        PADDING_X: 0,
        PADDING_Y: 0,
        SCALE: new PIXI.Point(1, 1)
    };

    // **  Reel Config ** //
    public static readonly REEL_ENGINE_CONFIG: IReelConfig = {

        // Creates Overflow Symbols.
        OVERFLOW: true,

        // How many symbols should be displayed as part of the winArea and how many reels there are.
        WINDOW_AMOUNT: 3,

        // How many extra Symbols will be added for the stop reel. Increasing the distance for the reel to stop which simplifies other easing stops.
        STOP_DISTANCE: 0,

        // Define Reelsets for the reels to use.
        REEL_SETS: ReelSetBuilder.createSpinReels({
                amountOfReels: 6,
                maxVisibleSize: 5,
                minimumSize: 50,
                fillerSymbols: {
                    amount: 5,
                    type: ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"],
                    maxAmountInView: 1
                }
            },
            [
                {
                    name: "NORMAL",
                    symbols: [
                        {
                            type: BasicSymbolType.MYSTERY,
                            amount: [1, 1, 1, 1, 1, 1],
                            excludeWithSymbol: [BasicSymbolType.WILD, BasicSymbolType.SCATTER, BasicSymbolType.XWILD],
                            maxAmountInView: 1
                        },
                        {
                            type: BasicSymbolType.WILD,
                            amount: [0, 1, 1, 1, 1, 1],
                            excludeWithSymbol: [BasicSymbolType.MYSTERY, BasicSymbolType.SCATTER, BasicSymbolType.XWILD],
                            maxAmountInView: 1
                        },
                        {
                            type: BasicSymbolType.SCATTER,
                            amount: [0, 1, 1, 1, 1, 1],
                            excludeWithSymbol: [BasicSymbolType.WILD, BasicSymbolType.MYSTERY, BasicSymbolType.XWILD],
                            maxAmountInView: 1
                        },
                        {
                            type: BasicSymbolType.XWILD,
                            amount: [0, 1, 1, 1, 1, 1],
                            excludeWithSymbol: [BasicSymbolType.WILD, BasicSymbolType.MYSTERY, BasicSymbolType.SCATTER],
                            maxAmountInView: 1
                        }
                    ]
                },
                {
                    name: "RESPIN",
                    symbols: [
                        {
                            type: BasicSymbolType.MYSTERY,
                            amount: [1, 1, 1, 1, 1, 1],
                            excludeWithSymbol: [BasicSymbolType.WILD, BasicSymbolType.SCATTER, BasicSymbolType.XWILD],
                            maxAmountInView: 1
                        },
                        {
                            type: BasicSymbolType.WILD,
                            amount: [0, 1, 1, 1, 1, 1],
                            excludeWithSymbol: [BasicSymbolType.MYSTERY, BasicSymbolType.SCATTER, BasicSymbolType.XWILD],
                            maxAmountInView: 1
                        },
                        {
                            type: BasicSymbolType.SCATTER,
                            amount: [0, 1, 1, 1, 1, 1],
                            excludeWithSymbol: [BasicSymbolType.WILD, BasicSymbolType.MYSTERY, BasicSymbolType.XWILD],
                            maxAmountInView: 1
                        },
                        {
                            type: BasicSymbolType.XWILD,
                            amount: [0, 1, 1, 1, 1, 1],
                            excludeWithSymbol: [BasicSymbolType.WILD, BasicSymbolType.MYSTERY, BasicSymbolType.SCATTER],
                            maxAmountInView: 1
                        }
                    ]
                },
                {
                    name: "FREESPIN_LEVEL_ONE",
                    symbols: [
                        {type: BasicSymbolType.MYSTERY, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0},
                        {type: BasicSymbolType.WILD, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0},
                        {type: BasicSymbolType.SCATTER, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0},
                        {type: BasicSymbolType.XWILD, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0}
                    ]
                },
                {
                    name: "FREESPIN_LEVEL_TWO",
                    symbols: [
                        {type: BasicSymbolType.MYSTERY, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0},
                        {type: BasicSymbolType.WILD, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0},
                        {type: BasicSymbolType.SCATTER, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0},
                        {type: BasicSymbolType.XWILD, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0}
                    ]
                },
                {
                    name: "FREESPIN_LEVEL_THREE",
                    symbols: [
                        {type: BasicSymbolType.MYSTERY, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0},
                        {type: BasicSymbolType.WILD, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0},
                        {type: BasicSymbolType.SCATTER, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0},
                        {type: BasicSymbolType.XWILD, amount: [0, 0, 0, 0, 0, 0], maxAmountInView: 0}
                    ]
                }
            ]
        ),

        DIM: {
            PADDING_X: 6,
            PADDING_Y: 0,
            WIDTH: 110,
            HEIGHT: 540,
        },
        // Define the duration for specific states.
        DURATION: {

            START: 0.15,
            FAST_START: 0.1,
            SPIN: 0.15 / 5,
            FAST_SPIN: 0.1 / 5,

            STOP: 0.3,
            FAST_STOP: 0.2,
            QUICK_STOP: 0.25,

            ATTENTION_STOP: 1.8,

            // Define the minimum time from the start -> stop;
            MINIMUM_SPIN_TIME: 3, // Normal
            MINIMUM_FAST_SPIN_TIME: 1, // Fast

            EXPAND: 0.25
        },
        // Define the delay for specific states.
        DELAY: {
            START: 0.0,
            FAST_START: 0.0,
            STOP: 0.1,
            FAST_STOP: 0.05,
            QUICK_STOP: 0.01,
        },
        // Define the Easeing for specific states.
        EASING: {
            START: Back.easeIn.config(1.5),
            FAST_START: Back.easeIn.config(1.25),
            STOP: Elastic.easeOut.config(0.8, 0.8),
            START_TEASER: CustomEase.create("teaserStart", "M0,0,C0,0,0.057,-0.003,0.09,-0.012,0.177,-0.037,0.231,-0.065,0.32,-0.092,0.355,-0.103,0.379,-0.106,0.415,-0.109,0.437,-0.111,0.453,-0.109,0.475,-0.106,0.495,-0.102,0.51,-0.099,0.527,-0.089,0.563,-0.069,0.589,-0.047,0.622,-0.022,0.65,0,0.7,0.042,0.776,0.042,0.836,0.042,0.877,0.019,0.9,0,0.942,-0.036,1,0,1,0"),

            FAST_STOP: Back.easeOut.config(0.7),
            QUICK_STOP: Back.easeOut.config(0.7),

            ATTENTION_STOP: Back.easeOut.config(0.2),
            // FAST_STOP: Elastic.easeOut.config(0.8, 0.55),
            // QUICK_STOP:  Elastic.easeOut.config(0.8, 0.55),

            EXPAND: Back.easeInOut.config(1.7),
            NUDGE: CustomEase.create("nudge", "M0,0 C0,0 0.264,-0.156 0.3,0 0.321,0.084 0.559,0.91 0.6,1 0.628,1.062 0.737,1.02 0.8,1 0.88,0.974 1,1 1,1"),
        }
    }

    public static readonly WIN_RATIOS: IWinRatios = {
        normalWin: [
            {
                ratio: 0,
                text: "Tiny Win",
                duration: 0,
            },
            {
                ratio: 1.01,
                text: "Small Win",
                duration: 0.1,
            },
            {
                ratio: 3,
                text: "Medium Win",
                duration: 0.2,
            },
            {
                ratio: 5,
                text: "Large Win",
                duration: 0.3,
            }
        ],
        bigWin: [
            {
                ratio: 15,
                text: "Big Win",
                duration: 5.5,
            },
            {
                ratio: 25,
                text: "Super Win",
                duration: 6,
            },
            {
                ratio: 50,
                text: "Mega Win",
                duration: 6,
            },
            {
                ratio: 100,
                text: "Epic Win",
                duration: 6,
            },
            {
                ratio: 200,
                text: "Wicked Win",
                duration: 14,
            }
        ]
    };

    public static readonly NO_WIN_GAME_DATA = {
        "reels": [
            ["L3", "L3", "L3", "L3", "M4"],
            ["L2", "L5", "L5", "L5", "L4"],
            ["M5", "L3", "L5", "L3", "M4"],
            ["M3", "L4", "L2", "L2", "L2"],
            ["M1", "L1", "L1", "L1", "L5"],
            ["L1", "L5", "M1", "M3", "L4"]
        ],
        "evaluatedArea": [
            ["L3", "L3", "L3"],
            ["L5", "L5", "L5"],
            ["L3", "L5", "L3"],
            ["L4", "L2", "L2"],
            ["L1", "L1", "L1"],
            ["BLANK", "BLANK", "BLANK"]
        ],
        "betWayWins": [],
        "totalSpinWinnings": 0,
        "accumulatedRoundWin": 0,
        "playedBetValue": 1,
        "reelsNextSpin": "BASE_REELSET",
        "mode": "NORMAL",
        "nextMode": "NORMAL",
        "numberOfFreespinsPlayed": 0,
        "freespinsLeft": 0,
        "scatterPositions": [-1, -1, -1, -1, -1, -1],
        "symbolSizes": [
            [1, 1, 1],
            [1, 1, 1],
            [1, 1, 1],
            [1, 1, 1],
            [1, 1, 1],
            [1, 1, 1]
        ],
        "freespinTriggeredThisSpin": false,
        "wasFeatureBuy": false,
        "boostedBetPlayed": false,
        "brokeTheBank": false,
        "winMultiplier": 1,
        "nAdditionalFsAwarded": 0,
        "topTrain": ["BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK"],
        "bottomTrain": ["BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK"],
        "mysterySymbolRevealed": "",
        "lockedSymbols": [
            [null, null, null],
            [null, null, null],
            [null, null, null],
            [null, null, null],
            [null, null, null],
            [null, null, null]
        ],
        "jumpingWildList": [],
        "burningTrain": [false, false, false],
        "outcomeFlagForQA": "",
        "outcomeFlagReelsForQA": [
            ["L3", "L3", "L3", null, null, null, null, null],
            ["L5", "L5", "L5", null, null, null, null, null],
            ["L3", "L5", "L3", null, null, null, null, null],
            ["L4", "L2", "L2", null, null, null, null, null],
            ["L1", "L1", "L1", null, null, null, null, null],
            [null, null, null, null, null, null, null, null]
        ]
    };
    public static readonly JW_EXTRA_SPINS_SHOW_DELAY: number = 0.167;
    public static FORCE_BRING_BOTTOM_TRAIN: boolean = false;
    public static TRAIN_PRESENTATION_SKIPPED: boolean = false;

    // #396
    public static xWILD_PRESENTATION_STARTED: boolean = false;
}